import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import {
  DriverOption,
  DriverPanelTab,
  hasDriverLocation,
} from "app/types/driver";

type DriverPanelProps = {
  open: boolean;
  onClose: () => void;
  drivers: DriverOption[];
  isLoading?: boolean;
  selectedDriverId?: number | null;
  onShowDriverLocation?: (driver: DriverOption) => void;
};

function getDriverInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
}

function DriverListItem({
  driver,
  selected,
  onShowDriverLocation,
}: {
  driver: DriverOption;
  selected: boolean;
  onShowDriverLocation?: (driver: DriverOption) => void;
}) {
  const canShowLocation = driver.is_on_duty && hasDriverLocation(driver);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderBottom: "1px solid #f1f5f9",
        bgcolor: selected ? "#f0fdf4" : "transparent",
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: "#dbeafe",
          color: "#1d4ed8",
          fontSize: "0.82rem",
          fontWeight: 700,
        }}
      >
        {getDriverInitials(driver.name)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.92rem",
            color: "#111827",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {driver.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#64748b", display: "block", lineHeight: 1.35 }}
        >
          @ {driver.id}
        </Typography>
      </Box>
      {driver.is_on_duty && (
        <Tooltip
          title={
            canShowLocation
              ? "Show driver on map"
              : "Location unavailable"
          }
        >
          <span>
            <IconButton
              aria-label={`Show ${driver.name} on map`}
              size="small"
              disabled={!canShowLocation}
              onClick={() => onShowDriverLocation?.(driver)}
              sx={{
                width: 32,
                height: 32,
                border: "1px solid #e5e9ef",
                borderRadius: "8px",
                bgcolor: selected ? "#dcfce7" : "#fafbfc",
                color: canShowLocation ? "#5fa67a" : "#cbd5e1",
              }}
            >
              <MyLocationIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}

export function DriverPanel({
  open,
  onClose,
  drivers,
  isLoading = false,
  selectedDriverId = null,
  onShowDriverLocation,
}: DriverPanelProps) {
  const [activeTab, setActiveTab] = useState<DriverPanelTab>("on");
  const [searchQuery, setSearchQuery] = useState("");

  const onDutyDrivers = useMemo(
    () => drivers.filter((driver) => driver.is_on_duty),
    [drivers]
  );
  const offDutyDrivers = useMemo(
    () => drivers.filter((driver) => !driver.is_on_duty),
    [drivers]
  );

  const visibleDrivers = useMemo(() => {
    const source = activeTab === "on" ? onDutyDrivers : offDutyDrivers;
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return source;
    }
    return source.filter(
      (driver) =>
        driver.name.toLowerCase().includes(query) ||
        String(driver.id).includes(query)
    );
  }, [activeTab, offDutyDrivers, onDutyDrivers, searchQuery]);

  if (!open) {
    return null;
  }

  return (
    <Box
      role="dialog"
      aria-label="Drivers panel"
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: { xs: "100%", sm: 340 },
        maxWidth: "100%",
        zIndex: 700,
        bgcolor: "#fff",
        borderLeft: "1px solid #e5e9ef",
        boxShadow: "-8px 0 28px rgba(15, 23, 42, 0.14)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          pt: 1.25,
          pb: 0.75,
          borderBottom: "1px solid #eef1f4",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>
          Drivers
        </Typography>
        <IconButton
          aria-label="Close drivers panel"
          size="small"
          onClick={onClose}
          sx={{
            width: 32,
            height: 32,
            border: "1px solid #e5e9ef",
            borderRadius: "8px",
            bgcolor: "#fafbfc",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 1.5, py: 1.25, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search drivers"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "#fafbfc",
              fontSize: "0.875rem",
            },
          }}
        />
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_event, value: DriverPanelTab) => setActiveTab(value)}
        variant="fullWidth"
        sx={{
          minHeight: 42,
          px: 1.5,
          borderBottom: "1px solid #eef1f4",
          flexShrink: 0,
          "& .MuiTab-root": {
            minHeight: 42,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.82rem",
          },
        }}
      >
        <Tab
          value="on"
          label={`ON (${onDutyDrivers.length})`}
          sx={{
            color: activeTab === "on" ? "#16a34a" : "#64748b",
          }}
        />
        <Tab
          value="off"
          label={`OFF (${offDutyDrivers.length})`}
          sx={{
            color: activeTab === "off" ? "#dc2626" : "#64748b",
          }}
        />
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress size={26} sx={{ color: "#5fa67a" }} />
          </Box>
        ) : visibleDrivers.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              {searchQuery.trim()
                ? "No drivers match your search."
                : activeTab === "on"
                  ? "No drivers on duty."
                  : "No drivers off duty."}
            </Typography>
          </Box>
        ) : (
          visibleDrivers.map((driver) => (
            <DriverListItem
              key={driver.id}
              driver={driver}
              selected={selectedDriverId === driver.id}
              onShowDriverLocation={onShowDriverLocation}
            />
          ))
        )}
      </Box>
    </Box>
  );
}
